/**
 * Shared Validation Functions
 *
 * Reusable validators for attribute values across all activities
 */

import type { ValidationResult } from "../../types"

/**
 * Validate that a value is a positive number
 *
 * @param value - String value to validate
 * @param fieldName - Name of field for error messages
 * @returns Validation result
 */
export const validatePositiveNumber = (value: string, fieldName: string): ValidationResult => {
  if (!value) {
    return { valid: false, error: `${fieldName} is required` }
  }

  const num = parseFloat(value)
  if (isNaN(num)) {
    return { valid: false, error: "Must be a valid number" }
  }
  if (num <= 0) {
    return { valid: false, error: `${fieldName} must be positive` }
  }
  return { valid: true }
}

/**
 * Validate quantity - no restrictions, just positive number check
 *
 * @param value - Quantity value to validate
 * @returns Validation result
 */
export const validateQuantity = (value: string): ValidationResult => {
  if (!value) return { valid: true } // Optional

  const num = parseFloat(value)
  if (isNaN(num)) {
    return { valid: false, error: "Must be a valid number" }
  }
  if (num < 0) {
    return { valid: false, error: "Quantity must be positive" }
  }
  return { valid: true }
}

/**
 * Validate percentage (0-100)
 *
 * @param value - Percentage value to validate
 * @returns Validation result
 */
export const validatePercentage = (value: string): ValidationResult => {
  if (!value) return { valid: true } // Optional field

  const num = parseFloat(value)
  if (isNaN(num)) {
    return { valid: false, error: "Must be a valid number" }
  }
  if (num < 0 || num > 100) {
    return { valid: false, error: "Must be between 0 and 100" }
  }
  return { valid: true }
}

/**
 * Validate temperature (-273°C to 100°C)
 *
 * @param value - Temperature value to validate
 * @returns Validation result
 */
export const validateTemperature = (value: string): ValidationResult => {
  if (!value) return { valid: true } // Optional field

  const num = parseFloat(value)
  if (isNaN(num)) {
    return { valid: false, error: "Must be a valid number" }
  }
  if (num < -273 || num > 100) {
    return { valid: false, error: "Temperature must be between -273°C and 100°C" }
  }
  return { valid: true }
}

/**
 * Validate pressure (0-700 bar)
 *
 * @param value - Pressure value to validate
 * @returns Validation result
 */
export const validatePressure = (value: string): ValidationResult => {
  if (!value) return { valid: true } // Optional field

  const num = parseFloat(value)
  if (isNaN(num)) {
    return { valid: false, error: "Must be a valid number" }
  }
  if (num < 0 || num > 700) {
    return { valid: false, error: "Pressure must be between 0 and 700 bar" }
  }
  return { valid: true }
}
